// Shows which planned meal a BBQ cook was for, and links / relinks / unlinks it.
// Used compactly in the cook list and in full on the cook detail page.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import EditIcon from '@material-ui/icons/Edit';
import CookService from '../../services/cook.service';
import { fmtDay, fmtDayOffset } from './cookFormat';

const useStyles = makeStyles((theme) => ({
  linked: { display: 'inline-flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' },
  auto: { color: '#888', fontSize: 12, marginLeft: 6 },
  muted: { color: '#888' },
  offset: { color: '#888', fontSize: 12, whiteSpace: 'nowrap', marginLeft: theme.spacing(2) },
  portions: { marginTop: theme.spacing(2) }
}));

const stop = e => e.stopPropagation();

const describePlan = p => [fmtDay(p.day), p.mealTime].filter(Boolean).join(' · ');

const LinkDialog = ({ cookId, current, onClose, onLinked }) => {
  const classes = useStyles();
  const [candidates, setCandidates] = useState(null);
  const [selected, setSelected] = useState(null);
  const [portions, setPortions] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    CookService.plannedMealCandidates(cookId)
      .then(res => {
        setCandidates(res.data);
        // Preselect the current link, else the closest plan that has portions, else the closest.
        const pick = res.data.find(c => current && c.id === current.id) ||
          res.data.find(c => c.portionsMade > 0) || res.data[0];
        if (pick) { setSelected(pick.id); setPortions(pick.portionsMade ?? ''); }
      })
      .catch(err => setError(err.response?.data?.message || 'Could not load planned meals.'));
  }, [cookId, current]);

  const choose = c => { setSelected(c.id); setPortions(c.portionsMade ?? ''); };

  const save = () => {
    CookService.linkPlannedMeal(cookId, selected, portions === '' ? undefined : portions)
      .then(() => { onLinked(); onClose(); })
      .catch(err => setError(err.response?.data?.message || 'Could not link.'));
  };

  return (
    <Dialog open onClose={onClose} onClick={stop} fullWidth maxWidth="sm">
      <DialogTitle>Which planned meal was this cook for?</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" style={{ marginBottom: 8 }}>{error}</Alert>}
        {candidates && candidates.length === 0 && (
          <Typography variant="body2" className={classes.muted}>
            Nothing is planned within three days of this cook. Add the meal in
            the <RouterLink to="/planner" onClick={onClose}>Planner</RouterLink>, then link it here.
          </Typography>
        )}
        {candidates && candidates.length > 0 && (
          <>
            <List dense disablePadding>
              {candidates.map(c => (
                <ListItem key={c.id} button selected={selected === c.id} onClick={() => choose(c)}>
                  <ListItemIcon style={{ minWidth: 36 }}>
                    <Radio edge="start" size="small" checked={selected === c.id} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={c.mealName || 'Unnamed meal'}
                    secondary={`${describePlan(c)}${c.portionsMade > 0 ? ` · ${c.portionsMade} portions made` : ''}`} />
                  <span className={classes.offset}>{fmtDayOffset(c.dayOffset)}</span>
                </ListItem>
              ))}
            </List>
            {selected && (
              <TextField className={classes.portions} label="Portions made" type="number" size="small"
                value={portions} onChange={e => setPortions(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Saved to the planned meal, and counted in cooked stock if the meal tracks it." />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" variant="contained" disabled={!selected} onClick={save}>Link</Button>
      </DialogActions>
    </Dialog>
  );
};

/*
 * cookId      the cook
 * plannedMeal the linked plan from the API ({ id, day, mealName, mealTime, portionsMade, source }) or null
 * onChange    called after link/unlink so the parent can reload
 * compact     list-cell variant
 */
const PlannedMealLink = ({ cookId, plannedMeal, onChange, compact }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const unlink = e => {
    stop(e);
    CookService.unlinkPlannedMeal(cookId).then(onChange);
  };
  const openDialog = e => { stop(e); setOpen(true); };

  const dialog = open &&
    <LinkDialog cookId={cookId} current={plannedMeal} onClose={() => setOpen(false)} onLinked={onChange} />;

  if (!plannedMeal) {
    return (
      <>
        <Button size="small" color="primary" startIcon={<LinkIcon />} onClick={openDialog}>
          {compact ? 'Link' : 'Link to planned meal'}
        </Button>
        {dialog}
      </>
    );
  }

  return (
    <span className={classes.linked}>
      <span>
        {plannedMeal.mealName}
        <span className={classes.muted}>{` · ${compact ? fmtDay(plannedMeal.day) : describePlan(plannedMeal)}`}</span>
        {!compact && plannedMeal.portionsMade > 0 && <span className={classes.muted}>{` · ${plannedMeal.portionsMade} portions`}</span>}
        {plannedMeal.source === 'auto' && <span className={classes.auto}>auto-linked</span>}
      </span>
      {!compact && (
        <>
          <Tooltip title="Change planned meal">
            <IconButton size="small" onClick={openDialog}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Unlink (won't be auto-linked again)">
            <IconButton size="small" onClick={unlink}><LinkOffIcon fontSize="small" /></IconButton>
          </Tooltip>
        </>
      )}
      {dialog}
    </span>
  );
};

export default PlannedMealLink;
